import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { notificationService } from '../../services/notification.service';
import { Notification } from '../../types';
import { Loading } from '../../components/ui/Loading';

export const NotificationsPage = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const result = await notificationService.getMemberNotifications({
        unread: filter === 'unread'
      });
      setNotifications(result.items);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      toast.success('Marked as read');
      loadNotifications();
    } catch (error: any) {
      toast.error(error.message || 'Failed to mark as read');
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'EMERGENCY':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ANNOUNCEMENT':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'REMINDER':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ALERT':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'EMERGENCY':
        return '🚨';
      case 'ANNOUNCEMENT':
        return '📢';
      case 'REMINDER':
        return '⏰';
      case 'ALERT':
        return '⚠️';
      default:
        return '🔔';
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-600">View your notifications and alerts</p>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 px-6 py-3 font-medium ${
              filter === 'all'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`flex-1 px-6 py-3 font-medium ${
              filter === 'unread'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Unread
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {notifications.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">
              No {filter === 'unread' ? 'unread' : ''} notifications
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white rounded-lg shadow p-6 border-l-4 ${getTypeColor(notification.type)} ${
                !notification.read ? 'bg-blue-50' : ''
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getTypeIcon(notification.type)}</span>
                  <div>
                    <h3 className="font-semibold">{notification.title}</h3>
                    <p className="text-sm text-gray-500">{formatDate(notification.createdAt)}</p>
                  </div>
                </div>
                {!notification.read && (
                  <button
                    onClick={() => handleMarkAsRead(notification.id)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Mark as read
                  </button>
                )}
              </div>
              <p className="text-gray-700 mt-2">{notification.message}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
